import { Injectable } from '@angular/core'
import { AppConfig } from '../app.config'
import * as JRPC from 'jrpc/jrpc.min.js'
import { BehaviorSubject, Observable, Subject, fromEvent, combineLatest, of } from 'rxjs'
import {
  take,
  tap,
  switchMap,
  map,
  takeUntil,
  filter,
  distinctUntilChanged,
  debounceTime,
  startWith,
  timeout,
} from 'rxjs/operators'
import { NetworkNode } from '../flight-network/shared'
import { ToastrService } from 'ngx-toastr'
import {
  CenterStatus,
  AppState,
  changeOpenState,
  ChecklistState,
  FlightNetworkCenterState,
  getUserCenterOnce,
  getUserCenterActiveVehicle,
  getFlightNetworkCenterById,
  VehicleState,
} from '../@store'
import { Store, select } from '@ngrx/store'
import { MarkerUpdateParams } from '../controls'
import { Router } from '@angular/router'

const CONNECTION_HEALTH_CHECK_INTERVAL_MS = 1000
const CONNECTION_HEALTH_CHECK_TIMEOUT_MS = 5 * 1000

export enum RPCMissionState {
  Created = 'created',
  Preflight = 'preflight',
  TakingOff = 'taking_off',
  InflightToDestination = 'inflight_to_destination',
  InflightHome = 'inflight_home',
  WaitingForWireDropConfirmation = 'waiting_for_wire_drop_confirmation',
  PerformingWireDrop = 'performing_wire_drop',
  LandingHome = 'landing_home',
  Completed = 'completed',
  Aborted = 'aborted',
  Postflight = 'postflight',
  HoveringPaused = 'hovering_paused',
  McPostflight = 'mc_postflight',
  SafingUp = 'safing_up',
  SafedUp = 'safed_up',
  AbortHome = 'abort_home',
  // TODO: remove, no server state is serialized as 'rtl'
  // 'fc_rtl' (which is the most similar) is not in use
  Rtl = 'rtl',
  EmergencyLand = 'emergency_landing',
  RcInControl = 'rc_in_control',
  ParachuteTriggered = 'parachute_triggered',
  VehicleDisconnected = 'vehicle_disconnected',
  FcLoss = 'fc_loss',
}

export type VehicleId = number
export type MissionId = string
export interface RPCPreflightMission {
  id: MissionId
  package_name: string
  package_id: number
  order_id: number
  order_name: string
  preflight_checklist_id: number
  started: boolean
  vehicle_id: VehicleId
  vehicle_name: string
}

export interface RPCPreflightOverview {
  weather_information: WeatherInformation | null
  battery_consumption_estimation: MissionBatteryConsumptionEstimation | null
  mc_error_message: string | null
}

interface MissionBatteryConsumptionEstimation {
  delivery_point_count: number
  dropoff_consumption_pct: number
  flight_distance_m: number
  distance_consumption_pct: number
  waypoint_count: number
  waypoint_consumption_pct: number
  total_consumption_pct: number
  is_sufficient: boolean
}

interface WeatherInformation {
  weather_conditions?: WeatherConditions
  error_text?: string
}

interface WeatherConditions {
  wind_speed_ms?: number
  wind_gust_speed_ms?: number
  precipitation_mm_hr?: number
  precipitation_probability_pct?: number
  temperature_deg_c?: number
}

export interface RPCPreflightMissionState {
  mission_id: MissionId
  takeoff_prohibition: string | null
}

export enum RPCPackageStatus {
  Created = 'created',
  Queued = 'queued',
  Assigned = 'assigned',
  Inflight = 'inflight',
  WaitingForReleaseApproval = 'waiting_for_release_approval',
  Releasing = 'releasing',
  Delivered = 'delivered',
  Canceled = 'canceled',
  Aborted = 'aborted',
}

export interface RPCMissionStateUpdate {
  mission_id: MissionId
  state: RPCMissionState
  package_status: RPCPackageStatus
  until: number | null
  vehicle_postflight_checklist_id?: number
  vehicle_id?: VehicleId
  is_failure_mode: boolean
  dropoff_approved: boolean
  package_name?: string
}

export interface RPCPackage {
  id: number
  name: string
  order_id: number
  order_name: string
  client_id: number
  client_name: string
  destination_node_id: number
  destination_node_name: string
  destination_node_uuid: string
  creation_timestamp: string
}

export interface RPCCallbackData {
  success: boolean
  data?: any
  reason?: string
}

export interface RPCOrder {
  id: number
  name: string
  client_id: number
  client_name: string
  destination_node_id: number
  destination_node_name: string
  destination_node_uuid: string
  creation_timestamp: string
}

export interface RPCMissionFlightData {
  battery_pct: number
  gps_signal_level: number
  location: {
    lat: number
    lng: number
    alt: number
  }
  mission_id: MissionId
  speed_ms: number
}

export enum RPCWeightUnits {
  KG = 'kg',
  LBS = 'lbs',
}

export interface RPCPackageForm {
  name: string
  weight: {
    weight: number
    units: RPCWeightUnits
  }
  order_id: number
}

export interface RPCScenarioForm {
  vehicle_id: VehicleId
  scenario: string
  delay: number
  params: object
}

export interface RPCMissionRouteData {
  mission_id: MissionId
  route: NetworkNode[]

  distance?: number
  params?: MarkerUpdateParams
  isPlannedRoute?: boolean
}

export interface RPCLogLine {
  mission_id: MissionId
  timestamp: Date
  message: string
  type: RPCLogLineType
}

export interface RPCSimulationScenario {
  simulationId: string
  description: string
  display_name: string
  params: {
    default_value: string
    description: string
    name: string
  }[]
}

export enum VehicleStatus {
  OutOfService = 'Out of Service',
  Online = 'Online',
  Offline = 'Offline',
}

export interface RPCVehicleStatus {
  id: VehicleId
  simulation_status: string
  status: VehicleStatus
  is_in_service: boolean
  is_parked: boolean
}

export interface VehicleExtendedState extends RPCVehicleStatus {
  isOnline: boolean
  isOffline: boolean
  isOperational: boolean
}

export interface RPCEtasData {
  mission_id: MissionId
  delivery_eta_sec: number
  landing_eta_sec: number
  time_since_takeoff_sec: number
  progress: number
}

export interface RPCChecklists {
  vehicles: RPCVehicle[]
  center_checklist: RPCCenter
}

export interface RPCVehicle {
  vehicle_id: VehicleId
  vehicle_name: string
  vehicle_checklist: RPCVehicleChecklist
}

export interface RPCVehicleChecklist {
  id: number
  name: string
  checklist_type: string
  instructions: ChecklistInstruction[]
}

export interface RPCCenter {
  id: number
  name: string
  checklist_type: string
  instructions: ChecklistInstruction[]
}

export interface ChecklistInstruction {
  caption: string
}

export type RPCLogLineType = 'warning' | 'error' | 'info'

export interface RPCCenterState {
  open_state: CenterStatus
  center_id: number
}

export interface RPCMissionNotificationData {
  level: string
  mission_id: MissionId
  text: string
}

export enum NextTaskType {
  CloseCenter = 'close_center',
  StartPreflight = 'start_preflight',
  CompletePreflight = 'complete_preflight',
  CompletePostflight = 'complete_postflight',
}

export interface NextTask {
  type: NextTaskType
  missionState?: RPCMissionStateUpdate
  preflightMission?: RPCPreflightMission
  postflightMission?: RPCMissionStateUpdate
  pendingPackage?: RPCPackage
  center?: FlightNetworkCenterState
  vehicle?: VehicleState
}

export interface WebSocketError {
  reason: string
  shouldReconnect: boolean
}

export interface RpicControls {
  mission_id: MissionId
  is_rpic_in_control: boolean
}

export interface LiveMissionObject {
  creation_timestamp: string
  mission_id: MissionId
  package_creation_timestamp: string
  slot: number
  vehicle_id: VehicleId
  vehicle_name: string
}

export interface WithMissionId {
  id: MissionId
}

export interface JrpcServiceParams {
  shouldUseSingleConnectionHere: boolean
}

interface JrpcErrorResponsePayload extends Error {
  code: number
  data?: {
    type: string
    message: string
    args: unknown[]
  }
  message: string
  method_id: string
  result: unknown
}

@Injectable({
  providedIn: 'root',
})
export class JrpcService {
  wsOpened$ = new BehaviorSubject(false)
  packageQueue$ = new BehaviorSubject<RPCPackage[]>([])
  orderQueue$ = new BehaviorSubject<RPCOrder[]>([])

  // undefined - initial value, before information about preflight has been received from the server.
  // null means server has updated that no preflight is in progress.
  preflightMission$ = new BehaviorSubject<RPCPreflightMission | null | undefined>(undefined)

  preflightOverview$ = new BehaviorSubject<RPCPreflightOverview | null>(null)

  preflightMissionState$ = new BehaviorSubject<RPCPreflightMissionState | null>(null)
  pendingPackage$ = new BehaviorSubject<RPCPackage | null>(null)
  missionsLogs$ = new BehaviorSubject<RPCLogLine[]>([])
  vehicleUpdated$ = new Subject<VehicleState>()
  vehicleDeleted$ = new Subject<{ id: number }>()
  missionFinished$ = new Subject<WithMissionId>()
  centerState$ = new BehaviorSubject<CenterStatus | null>(null)
  webSocketError$ = new BehaviorSubject<WebSocketError | null>(null)

  missionsState$ = new BehaviorSubject<Record<string, RPCMissionStateUpdate>>({})
  missionsFlightData$ = new BehaviorSubject<Record<string, RPCMissionFlightData>>({})
  missionsRoutesData$ = new BehaviorSubject<Record<string, RPCMissionRouteData>>({})
  currentEtas$ = new BehaviorSubject<Record<string, RPCEtasData>>({})
  vehiclesStatuses$ = new BehaviorSubject<Record<VehicleId, VehicleExtendedState>>({})
  operationalVehicles$ = this.vehiclesStatuses$.pipe(
    map((vehicleIdToExtendedStateRecord) =>
      Object.values(vehicleIdToExtendedStateRecord).filter(
        (vehicleExtendedState) => vehicleExtendedState.isOperational,
      ),
    ),
  )
  missionsNotificationData$ = new BehaviorSubject<Record<string, RPCMissionNotificationData>>({})
  missionsRpicControls$ = new BehaviorSubject<Record<string, RpicControls>>({})
  liveMissionsData$ = new BehaviorSubject<Record<string, LiveMissionObject>>({})

  nextTask$: Observable<NextTask> = this.createNextTaskObservable()

  heartBeatTimeout: ReturnType<typeof window.setTimeout> | null = null

  private remote: JRPC
  private ws: WebSocket | null = null
  private reconnectionUrl: URL
  private reconnectIntervalId: ReturnType<typeof window.setInterval> | null = null
  private reconnectIntervalMs = 5000
  private nextTaskTickDebounceTime = 100
  private hadConnectionProblem = false
  private destroy$: Subject<void>

  constructor(
    private appConfig: AppConfig,
    private toastr: ToastrService,
    private store: Store<AppState>,
    private router: Router,
  ) {
    this.initWebSocketRefreshController()
  }

  private initWebSocketRefreshController(): void {
    this.wsOpened$.pipe(distinctUntilChanged()).subscribe(this.reconnectOnWebSocketOpenIfNeeded)
  }

  private reconnectOnWebSocketOpenIfNeeded = (opened: boolean): void => {
    if (!opened) {
      return
    }

    // When web-socket is alive again after connection lost, refresh the whole page to get all data
    if (this.hadConnectionProblem) {
      this.reloadApplication(new URL(window.location.href))
    }
  }

  reconnectWebSocketUseSingleConnectionHere = () => {
    this.initWS({ shouldUseSingleConnectionHere: true })
  }

  reloadApplication(reloadUrl: URL): void {
    // Our code currently does not support refreshing the UI properly
    // when we reconnect the web socket, except through reloading the page.
    // We add a random value as a get param to bypass caching.
    // Note:
    // In the case of clicking "Use Here",
    // we will technically disconnect and reconnect again due to this reload.
    // Because we connected once above and passed use-here to the BE
    // the other clients are already disconnected at this point. Therefore the reconnection
    // upon reload should work without passing use-here again to the BE.
    if (!reloadUrl) {
      console.error('cannot reconnect web socket without a reconnection url')
      reloadUrl = this.extractReconnectionURL()
    }
    const url = new URL(reloadUrl.href)
    url.searchParams.delete('refreshToken')
    url.searchParams.append('refreshToken', `${Math.random()}`)
    console.log('reloading the page due to reconnection of web socket')
    window.location.href = url.href
  }

  initService(): void {
    this.destroy$ = new Subject()
    this.initJRPC()
    this.initWS({ shouldUseSingleConnectionHere: false })
    this.initSubscriptions()
  }

  destroyService(): void {
    this.destroy$.next()
    this.destroy$.complete()
    clearTimeout(this.heartBeatTimeout)
    window.clearInterval(this.reconnectIntervalId)
    if (this.ws) {
      this.ws?.close()
      this.ws = null
    }
  }

  handleHealthCheckError = (error: Error): void => {
    console.warn(error)
    this.hadConnectionProblem = true
    this.wsOpened$.next(false)
  }

  subscribeToHeartBeatTimeout = (): void => {
    this.heart_beat_subscribe()
      .pipe(takeUntil(this.destroy$))
      .pipe(timeout(CONNECTION_HEALTH_CHECK_TIMEOUT_MS))
      .subscribe(this.sendHeartBeatPeriodically, this.handleHealthCheckError)
  }

  sendHeartBeatPeriodically = (): void => {
    this.heartBeatTimeout = setTimeout(this.subscribeToHeartBeatTimeout, CONNECTION_HEALTH_CHECK_INTERVAL_MS)
  }

  initWS(jrpcServiceParams: JrpcServiceParams): void {
    this.ws?.close()
    const websocketUrl = new URL(this.appConfig.base_websocket)
    if (jrpcServiceParams.shouldUseSingleConnectionHere) {
      websocketUrl.searchParams.append(this.appConfig.websocket_use_here_param, '')
    }
    this.ws = new WebSocket(websocketUrl.href)

    if (jrpcServiceParams.shouldUseSingleConnectionHere) {
      // We will reload upon grabbing the web socket (see explanation in reloadApplication).
      // We use the reconnection url that was saved before we navigated to use-here.
      this.ws.onopen = () => this.reloadApplication(this.reconnectionUrl)
      // If we get an error or get closed down - we also reload.
      // This is to prevent cases where we are logged out and now stuck
      // with use-here that always fails.
      this.ws.onerror = () => this.reloadApplication(this.reconnectionUrl)
      this.ws.onclose = () => this.reloadApplication(this.reconnectionUrl)
      return
    }

    this.ws.onopen = () => {
      this.wsOpened$.next(true)
      if (this.remote) {
        // Send queued messages a single time
        this.remote.setTransmitter((msg: string, next: (arg: boolean) => void) => {
          try {
            if (this.ws?.readyState === WebSocket.OPEN) {
              this.ws.send(msg)
              return next(false)
            }
            return next(true)
          } catch (error) {
            const message = 'Error transmitting JRPC message'
            this.toastr.error(message)
            console.error(message, error)
            return next(true)
          }
        })
        this.sendHeartBeatPeriodically()
        this.center_open_state_subscribe()
        this.package_queue_subscribe()
        this.preflight_mission_subscribe()
        this.live_missions_subscribe()
        this.vehicle_status_subscribe()
        this.reserved_orders_subscribe()
        this.pending_package_subscribe()
      }
    }

    // Hand off whatever WebSocket receives to JRPC
    this.ws.onmessage = (ev: MessageEvent): void => {
      this.remote?.receive(ev.data)
    }

    this.ws.onerror = (error): void => {
      this.hadConnectionProblem = true
      const message = this.appConfig.config.messages.jrpc_service.error.ws_lost_connection
      this.toastr.error(message)
      console.error(message, error)
      this.webSocketError$.next({
        reason: `${message} (code=${error})`,
        shouldReconnect: false,
      })
      this.wsOpened$.next(false)
      clearTimeout(this.heartBeatTimeout)
    }

    this.ws.onclose = this.handleSocketCloseEvent
  }

  private handleSocketCloseEvent = (ev: CloseEvent) => {
    if (4000 <= ev.code && ev.code <= 4099) {
      console.log(`web socket closed reason: ${ev.reason} (code=${ev.code})`)
      this.destroyService()
      if (ev.code === this.appConfig.websocket_close_code_multiple_connections) {
        // multiple device connections
        this.reconnectionUrl = this.extractReconnectionURL()
        this.router.navigate(['/use-here'])
      } else {
        this.router.navigate(['/login'])
      }
      this.webSocketError$.next({
        reason: `${ev.reason} (code=${ev.code})`,
        shouldReconnect: false,
      })
    }
    this.wsOpened$.next(false)
    clearTimeout(this.heartBeatTimeout)
  }

  private extractReconnectionURL(): URL {
    const reconnectionUrl = new URL(window.location.href)
    // In the case of the user manually refreshing the /use-here page
    // we might be assigning here the reconnection url to be /use-here page
    // The following code attempts to avoid this by truncating the url
    // if it contains the /use-here page.
    // This is disgusting but I could not find a better way around it.
    const indexOfUseHereSubUrl = reconnectionUrl.pathname.indexOf('use-here')
    if (indexOfUseHereSubUrl > 0) {
      reconnectionUrl.pathname = reconnectionUrl.pathname.substr(0, indexOfUseHereSubUrl)
    }
    return reconnectionUrl
  }

  private setVehicleStatuses = (vehicleStatusUpdate: RPCVehicleStatus): void => {
    const isOnline = vehicleStatusUpdate.status === VehicleStatus.Online
    const isOffline = vehicleStatusUpdate.status === VehicleStatus.Offline
    const vehicleExtendedState: VehicleExtendedState = {
      ...vehicleStatusUpdate,
      isOnline,
      isOffline,
      // A vehicle is offline when its Mission Computer is not connected to the Mission Manager.
      // When the RPIC starts the day-open, none of the MCs are typically even on.
      // Which is why offline vehicles go through day open.
      isOperational: vehicleStatusUpdate.is_in_service && !vehicleStatusUpdate.is_parked,
    }
    const vehicleStatuses = {
      ...this.vehiclesStatuses$.value,
      [vehicleStatusUpdate.id]: vehicleExtendedState,
    }
    this.vehiclesStatuses$.next(vehicleStatuses)
  }

  private setPreflightOverview = (preflightOverview: RPCPreflightOverview): void => {
    this.preflightOverview$.next(preflightOverview)
  }

  initJRPC(): void {
    this.remote = new JRPC({ client: true })

    this.remote_expose('package_queue_updated', (package_queue: RPCPackage[]) => {
      this.packageQueue$.next(package_queue)
    })

    this.remote_expose('pending_package_updated', (pending_package: RPCPackage) => {
      this.pendingPackage$.next(pending_package)
    })

    this.remote_expose('reserved_orders_updated', (order_queue: RPCOrder[]) => {
      this.orderQueue$.next(order_queue)
    })

    this.remote_expose('preflight_mission_at_center_changed', (mission: RPCPreflightMission) => {
      this.preflightMission$.next(mission)
    })

    this.remote_expose('preflight_overview_changed', this.setPreflightOverview)

    this.remote_expose('live_mission.created', (update: LiveMissionObject): void => {
      const { value } = this.liveMissionsData$
      this.liveMissionsData$.next({
        ...value,
        [update.mission_id]: update,
      })
      this.live_mission_info_subscribe(update['mission_id'])
    })

    this.remote_expose('mission_state_updated', (update: RPCMissionStateUpdate) => {
      this.addMissionLog(update.mission_id, 'Mission status: ' + update.state, update.is_failure_mode)
      const states = this.missionsState$.value
      this.missionsState$.next({
        ...states,
        [update.mission_id]: update,
      })
    })

    this.remote_expose('mission_accepted', (notification: any) => {
      const mission_id = notification['mission_id']
      const vehicle_id = notification['vehicle_id']
      let log: string
      if (vehicle_id) {
        // If the page is refreshed after the mission is finished, only `mission_id` is defined.
        log = `Vehicle ${vehicle_id} accepted mission`
      } else {
        log = `Mission accepted`
      }
      this.addMissionLog(mission_id, log)
    })

    this.remote_expose('mission_rejected', (notification: any) => {
      const mission_id = notification['mission_id']
      const vehicle_id = notification['vehicle_id']
      const reason = notification['reason']
      this.addMissionLog(mission_id, 'Vehicle ' + vehicle_id + ' rejected mission: ' + reason)
    })

    this.remote_expose('mission_preflight_state', (state: RPCPreflightMissionState) => {
      this.preflightMissionState$.next(state)
      const mission_id = state['mission_id']
      const takeoff_prohibition = state['takeoff_prohibition']
      if (!takeoff_prohibition) {
        this.addMissionLog(mission_id, 'Ready for takeoff')
      } else {
        this.addMissionLog(mission_id, 'Takeoff prohibited: ' + takeoff_prohibition)
      }
    })

    this.remote_expose('mission_waypoint_reached', (waypoint: any) => {
      const mission_id = waypoint['mission_id']
      const waypoint_index = waypoint['index']
      this.addMissionLog(mission_id, 'Reached waypoint ' + waypoint_index)
    })

    this.remote_expose('vehicle_status_updated', this.setVehicleStatuses)

    this.remote_expose('vehicle_updated', (vehicle_updated: VehicleState) => {
      this.store
        .select(getFlightNetworkCenterById, { id: vehicle_updated.center_id })
        .pipe(take(1))
        .subscribe((center) => {
          vehicle_updated.center_name = center.name
          this.vehicleUpdated$.next(vehicle_updated)
        })
    })

    this.remote_expose('vehicle_deleted', (vehicle_deleted: { id: VehicleId }) => {
      this.vehicleDeleted$.next(vehicle_deleted)
    })

    this.remote_expose('mission_text_message_received', (text_message: RPCMissionNotificationData) => {
      const value = this.missionsNotificationData$.value
      const mission_id = text_message['mission_id']
      const level = text_message['level']
      const text = text_message['text']
      this.missionsNotificationData$.next({
        ...value,
        [mission_id]: text_message,
      })
      this.addMissionLog(mission_id, level + ': ' + text)
    })

    this.remote_expose('mission_flight_data', (mission_flight_data: RPCMissionFlightData) => {
      const value = this.missionsFlightData$.value
      this.missionsFlightData$.next({
        ...value,
        [mission_flight_data.mission_id]: mission_flight_data,
      })
    })

    this.remote_expose('mission_route', (mission_route: RPCMissionRouteData) => {
      mission_route.route = mission_route.route.map((item) => new NetworkNode(item))
      const routes = this.missionsRoutesData$.value
      this.missionsRoutesData$.next({
        ...routes,
        [mission_route.mission_id]: mission_route,
      })
    })

    this.remote_expose('mission_etas', (etas_data: RPCEtasData) => {
      const { value } = this.currentEtas$
      this.currentEtas$.next({
        ...value,
        [etas_data.mission_id]: etas_data,
      })
    })

    this.remote_expose('live_mission.finished', (update: { mission_id: string }) => {
      const logs = this.missionsLogs$.value.filter((log) => log.mission_id !== update.mission_id)
      this.missionsLogs$.next(logs)

      const missions = this.missionsFlightData$.value
      delete missions[update.mission_id]

      this.missionsFlightData$.next(missions)

      const routes = this.missionsRoutesData$.value
      delete routes[update.mission_id]

      this.missionsRoutesData$.next(routes)

      const etas = this.currentEtas$.value
      delete etas[update.mission_id]

      this.currentEtas$.next(etas)

      const notifications = this.missionsNotificationData$.value
      delete notifications[update.mission_id]

      this.missionsNotificationData$.next(notifications)

      const controls = this.missionsRpicControls$.value
      delete controls[update.mission_id]

      this.missionsRpicControls$.next(controls)

      const liveMissions = this.liveMissionsData$.value
      delete liveMissions[update.mission_id]

      this.liveMissionsData$.next(liveMissions)

      const missionsState = this.missionsState$.value
      delete missionsState[update.mission_id]

      this.missionsState$.next(missionsState)

      this.missionFinished$.next({ id: update.mission_id })
    })

    this.remote_expose('center_open_state_updated', (new_open_state_update: RPCCenterState) => {
      const { open_state, center_id } = new_open_state_update
      this.centerState$.next(open_state)
      this.store.dispatch(changeOpenState({ open_state, center_id }))
    })

    this.remote_expose('mission_rpic_in_control_updated', (update: RpicControls) => {
      const { value } = this.missionsRpicControls$
      this.missionsRpicControls$.next({
        ...value,
        [update.mission_id]: update,
      })
    })
  }

  initSubscriptions(): void {
    this.wsOpened$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((opened) => {
      if (opened) {
        window.clearInterval(this.reconnectIntervalId)
      } else {
        this.reconnectIntervalId = window.setInterval(() => {
          this.initWS({ shouldUseSingleConnectionHere: false })
        }, this.reconnectIntervalMs) as unknown as ReturnType<typeof window.setInterval>
      }
    })

    /**
     * Let's also check websocket status when user focus on tab with our App.
     * This can be useful if WS was closed somehow when user was not in other tab or not in browser.
     * In this case, when user is back to our tab he will be reconnected to ws immediately.
     */
    fromEvent(window, 'focus')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
          this.initWS({ shouldUseSingleConnectionHere: false })
        }
      })

    /**
     * Here we check the connection of our tab. If it is lost - we manually close websocket
     * And reconnection cycle will be enabled automatically
     */
    fromEvent(window, 'offline')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.ws?.close()
        this.ws = null
      })
  }

  requestVehicleLogUpload(missionId: string, logType: string) {
    return this.remote_call('mission_computer.request_log_upload', [
      {
        mission_id: missionId,
        log_type: logType,
      },
    ])
  }

  addPackage(pack: RPCPackageForm): Observable<RPCPackage[]> {
    return this.remote_call('package_queue.create', [pack], `Couldn't add package due to an error.`).pipe(
      switchMap(() => this.packageQueue$.pipe(take(1))),
    )
  }

  removePackage(id: number): Observable<any> {
    return this.remote_call('package_queue.cancel', [{ id }], `Couldn't remove package due to an error.`)
  }

  reorderPackage(id: number, new_position: number): Observable<any> {
    return this.remote_call(
      'package_queue.reorder',
      [{ id, new_position }],
      `Couldn't reorder packages due to an error.`,
    )
  }

  approveTakeoff(mission_id: string): Observable<any> {
    return this.remote_call(
      'preflight_mission.approve_takeoff',
      [mission_id],
      `Couldn't takeoff due to an error.`,
    ).pipe(tap(() => this.preflightMission$.next(null)))
  }

  approveDropoff(mission_id: string): Observable<any> {
    return this.remote_call('mission.approve_dropoff', [mission_id], `Couldn't approve drop off due to an error.`)
  }

  createMission(package_id: number) {
    return this.remote_call(
      'pending_package.create_mission',
      [{ package_id }],
      `Couldn't create new mission due to an error.`,
    )
  }

  safeUp(mission_id: string): Observable<any> {
    return this.remote_call('mission.safe_up', [mission_id], `Couldn't abort mission and safe up due to an error.`)
  }

  abortAndReturnHome(mission_id: string): Observable<any> {
    return this.remote_call(
      'mission.abort_mission_return_home',
      [mission_id],
      `Couldn't abort mission and return home due to an error.`,
    )
  }

  abortAndLand(mission_id: string): Observable<any> {
    return this.remote_call(
      'mission.abort_mission_land',
      [mission_id],
      `Couldn't abort mission and land due to an error.`,
    )
  }

  triggerParachute(mission_id: string): Observable<any> {
    return this.remote_call(
      'mission.trigger_parachute',
      [mission_id],
      `Couldn't abort mission and trigger the parachute due to an error.`,
    )
  }

  gotoOperatorPostflight(mission_id: string): Observable<any> {
    return this.remote_call(
      'mission.goto_operator_postflight',
      [mission_id],
      `Couldn't go to postflight due to an error.`,
    )
  }

  startVehicleSimulation({ vehicle_id, scenario, params, delay }: RPCScenarioForm) {
    return this.remote_call(
      'simulated_vehicle.start',
      [vehicle_id, scenario, params, delay],
      "Couldn't start vehicle simulation due to an error.",
    )
  }

  stopVehicleSimulation(vehicleId: VehicleId) {
    return this.remote_call('simulated_vehicle.stop', [vehicleId], "Couldn't stop vehicle simulation due to an error.")
  }

  startPreflight(mission_id: string, checklist: ChecklistState) {
    return this.remote_call(
      'preflight_mission.start_preflight',
      [{ mission_id, checklist }],
      `Couldn't start preflight due to an error.`,
    )
  }

  subscribeToMissionOverviewUpdate = (): Observable<void> =>
    this.remote_call('preflight_mission.overview_subscribe', [], 'Error subscribing to preflight overview updates.')

  unsubscribeFromMissionOverviewUpdate = (): Observable<void> =>
    this.remote_call(
      'preflight_mission.overview_unsubscribe',
      [],
      'Error unsubscribing from preflight overview updates.',
    )

  cancelPreflight(missionId: MissionId, shouldRequeuePackage = false) {
    return this.remote_call(
      'preflight_mission.cancel',
      [missionId, shouldRequeuePackage],
      'Error cancelling preflight.',
    )
  }

  getSimulationScenarios(): Observable<RPCSimulationScenario[]> {
    return this.remote_call('simulation_scenario.list', [], "Couldn't get simulation scenarios due to an error.").pipe(
      map((scenarios: Record<string, RPCSimulationScenario>) => {
        return Object.entries(scenarios).map(([simulationId, simulationScenario]) => ({
          simulationId,
          ...simulationScenario,
        }))
      }),
    )
  }

  finishPostFlight(mission_id: string, checklist: ChecklistState): Observable<any> {
    return this.remote_call(
      'mission.finish_postflight',
      [{ mission_id, checklist }],
      `Couldn't finish postflight due to an error.`,
    )
  }

  createOrder(destination_node_uuid: any, name: string) {
    return this.remote_call(
      'reserved_orders.create',
      [{ name, destination_node_uuid }],
      `Couldn't create order due to an error.`,
    )
  }

  cancelOrder(order_id: number) {
    return this.remote_call('reserved_orders.cancel', [{ id: order_id }], `Couldn't cancel order due to an error.`)
  }

  setCenterState(open_state: CenterStatus, checklists: RPCChecklists) {
    return this.remote_call('center_open_state.set', [{ open_state, checklists }], `Couldn't due to an error.`)
  }

  setVehicleServiceState(id: VehicleId, is_in_service: boolean) {
    return this.remote_call(
      'vehicle_status.set_service_state',
      [{ id, is_in_service }],
      `Couldn't set vehicle service state due to an error.`,
    )
  }

  setVehicleParkedState(id: VehicleId, is_parked: boolean) {
    return this.remote_call(
      'vehicle_status.set_parked_state',
      [{ id, is_parked }],
      `Couldn't set vehicle parked state due to an error.`,
    )
  }

  getCenterOpenStateChecklists(open_state: CenterStatus): Observable<RPCChecklists> {
    return this.remote_call(
      'center_open_state.get_checklists',
      [{ open_state }],
      "Couldn't get checklists due to an error.",
    )
  }

  getPreflightChecklist(mission_id: string): Observable<ChecklistState> {
    return this.remote_call(
      'preflight_mission.get_preflight_checklist',
      [{ mission_id }],
      `Couldn't get preflight checklist due to an error.`,
    )
  }

  getPostflightChecklist(mission_id: string): Observable<ChecklistState> {
    return this.remote_call(
      'mission.get_postflight_checklist',
      [{ mission_id }],
      `Couldn't get postflight checklist due to an error.`,
    )
  }

  subscribeToCenterStateUpdates(): void {
    this.wsOpened$
      .pipe(
        filter((item) => Boolean(item)),
        take(1),
      )
      .subscribe(() => {
        this.center_open_state_subscribe()
      })
  }

  unsubscribeFromCenterStateUpdates(): void {
    this.wsOpened$
      .pipe(
        filter((item) => Boolean(item)),
        take(1),
      )
      .subscribe(() => {
        this.center_open_state_unsubscribe()
      })
  }

  private addMissionLog(mission_id: string, message: string, isFailureMode: boolean = false) {
    const lowerCaseMessage = message.toLowerCase()
    const isErrorMessage = lowerCaseMessage.includes('error')
    const isWarningMessage = lowerCaseMessage.includes('warn')
    let type: RPCLogLineType
    if (isErrorMessage || isFailureMode) {
      type = 'error'
    } else if (isWarningMessage) {
      type = 'warning'
    } else {
      type = 'info'
    }
    const logs = [
      ...this.missionsLogs$.value,
      {
        timestamp: new Date(),
        mission_id,
        message,
        type,
      },
    ]
    this.missionsLogs$.next(logs)
  }

  private remote_call(method_id: string, args: any[], errorMessage = ''): Observable<any> {
    let maybeSerializedArgs: string
    try {
      maybeSerializedArgs = JSON.stringify(args)
    } catch (ex) {
      maybeSerializedArgs = args.toString()
    }
    const callText = `${method_id}(${maybeSerializedArgs})`
    const subject = new Subject()

    new Observable((observer) => {
      if (this.remote) {
        this.remote.call(method_id, args, (error: JrpcErrorResponsePayload, result: unknown) => {
          if (error) {
            console.error(
              `Error in calling ${callText}\n\n`,
              {
                message: error.message,
                error,
                result,
                method_id,
                args,
              },
              `\n\n${error.data?.message}`,
            )
            observer.error({ message: error.message, error })
            if (errorMessage) {
              /**
               * Given an `errorMessage` from the caller we toast that, but first
               * try to show the backend error, unless it is `"None" or an empty string`.
               */
              this.toastr.error(error.message === 'None' ? errorMessage : error.message || errorMessage)
            }
          } else {
            observer.next(result)
          }
          observer.complete()
        })
      }
    }).subscribe(subject)

    return new Observable((observer) => subject.subscribe(observer))
  }

  private remote_expose(method_id: string, callback: (cbArg: unknown) => void) {
    if (this.remote) {
      this.remote.expose(method_id, function remoteExposeCallback(args, next) {
        callback(args)
        next(true)
      })
    }
  }

  private package_queue_subscribe() {
    this.remote_call('package_queue.subscribe', [])
  }

  private preflight_mission_subscribe() {
    this.remote_call('preflight_mission.subscribe', [])
  }

  private live_missions_subscribe() {
    this.remote_call('live_missions.subscribe', [])
  }

  private live_mission_info_subscribe(mission_id: string) {
    this.remote_call('mission.live_info.subscribe', [mission_id])
  }

  private vehicle_status_subscribe() {
    this.remote_call('vehicle_status.subscribe', [])
  }

  private reserved_orders_subscribe() {
    this.remote_call('reserved_orders.subscribe', [])
  }

  private pending_package_subscribe() {
    this.remote_call('pending_package.subscribe', [])
  }

  private center_open_state_subscribe() {
    this.remote_call('center_open_state.subscribe', [])
  }

  private center_open_state_unsubscribe() {
    this.remote_call('center_open_state.unsubscribe', [])
  }

  private heart_beat_subscribe(): Observable<unknown> {
    return this.remote_call('rpic_disconnection.echo', ['echo'])
  }

  private createNextTaskObservable(): Observable<NextTask | null> {
    return combineLatest([this.missionsState$, this.preflightMission$, this.centerState$, this.pendingPackage$]).pipe(
      switchMap(([missionsState, preflightMission, centerState, pendingPackage]) => {
        if (centerState === CenterStatus.CanBeClosed) {
          return this.store.pipe(getUserCenterOnce).pipe(
            map((center) => ({
              type: NextTaskType.CloseCenter,
              center,
            })),
          )
        }

        const postflightMissionId = Object.keys(missionsState).find((missionId) => {
          return missionsState[missionId].state === 'postflight'
        })

        if (postflightMissionId) {
          const mission = missionsState[postflightMissionId]
          return this.store.pipe(
            select(getUserCenterActiveVehicle, { vehicle_id: mission.vehicle_id }),
            take(1),
            map((vehicle) => ({
              type: NextTaskType.CompletePostflight,
              postflightMission: mission,
              vehicle,
            })),
          )
        }

        // Preflight and pending package tasks are less prioritized than postflight
        if (pendingPackage) {
          return of({
            type: NextTaskType.StartPreflight,
            pendingPackage,
          })
        }

        if (preflightMission) {
          return this.store.pipe(
            select(getUserCenterActiveVehicle, { vehicle_id: preflightMission.vehicle_id }),
            take(1),
            map((vehicle) => ({
              type: NextTaskType.CompletePreflight,
              pendingPackage,
              preflightMission,
              vehicle,
            })),
          )
        }
        return of(null)
      }),
      debounceTime(this.nextTaskTickDebounceTime),
      startWith<NextTask, NextTask>(null),
    )
  }
}
